import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FileText, Users, CreditCard, CalendarCheck, UserCog, Receipt, Menu, X, LogOut } from "lucide-react";
import { Footer, HowItWorks } from "@bc/ui";
import { Button } from "../ui/button";
import api from "../../services/api";
import VerifyEmailScreen from "./VerifyEmailScreen";

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("bidconvey_user") || "null");
  } catch {
    return null;
  }
}

const NAV_ITEMS = [
  {
    to: "/",
    label: "Request Quote",
    end: true,
  },
  {
    to: "/initial-quotations",
    label: "Quotations",
    icon: FileText,
    showCount: true,
  },
  {
    to: "/sellers-choice",
    label: "Sellers Choice",
    icon: Users,
    showSellersChoiceCount: true,
  },
  {
    to: "/payment-details",
    label: "Payment Details",
    icon: CreditCard,
  },
  {
    to: "/my-payments",
    label: "My Payments",
    icon: Receipt,
  },
  {
    to: "/appointment-confirmed",
    label: "Conveyancer Confirmed",
    icon: CalendarCheck,
  },
  {
    to: "/profile",
    label: "Profile",
    icon: UserCog,
  },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef(null);

  // The <main> pane is the only scroll container in the dashboard frame, and
  // it keeps its scroll position across route changes — so a new page would
  // open wherever the previous one was scrolled to. Reset it on navigation.
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const [quotationsCount, setQuotationsCount] = useState(null);
  const [sellersChoiceCount, setSellersChoiceCount] = useState(0);

  const [isAuthenticated, setIsAuthenticated] = useState(
    () => Boolean(localStorage.getItem("bidconvey_token")),
  );
  const [user, setUser] = useState(() => readStoredUser());

  // Re-check auth on route changes AND on cross-tab / same-tab auth events.
  // Calculator dispatches "bidconvey-auth-changed" after login / register /
  // logout so the chrome shows/hides without a full page reload. The same
  // event fires after the verify gate refreshes the stored user, so re-read it.
  useEffect(() => {
    const check = () => {
      setIsAuthenticated(Boolean(localStorage.getItem("bidconvey_token")));
      setUser(readStoredUser());
    };
    check();
    window.addEventListener("storage", check);
    window.addEventListener("bidconvey-auth-changed", check);
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("bidconvey-auth-changed", check);
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("bidconvey_token");
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get("/my-quotations");
        if (cancelled) return;
        const count = (data || []).reduce(
          (sum, item) => sum + ((item.quotations || []).length),
          0,
        );
        setQuotationsCount(count);
        const scCount = (data || []).filter(
          (item) => item.selection?.awaiting_buyer_choice && !item.selection?.buyers_final_choice
        ).length;
        setSellersChoiceCount(scCount);
      } catch {
        // Silent — the sidebar badge is best-effort.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem("bidconvey_token");
    localStorage.removeItem("bidconvey_user");
    window.dispatchEvent(new Event("bidconvey-auth-changed"));
    navigate("/");
    window.location.reload();
  };

  // Unauthenticated: render the route (e.g. Calculator's login/register view)
  // without the top bar or sidebar — there's nothing to log out of yet.
  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-gray-50">
          <Outlet />
        </div>
        <Footer />
      </>
    );
  }

  // Authenticated but unverified: block the entire app shell behind the verify
  // gate. Strict `=== false` so users whose stored object predates the field
  // (no is_verified) aren't gated.
  if (user && user.is_verified === false) {
    return (
      <>
        <VerifyEmailScreen user={user} />
        <Footer />
      </>
    );
  }

  return (
    // 100dvh tracks the *visible* mobile viewport (URL bar shown or hidden),
    // so the frame never exceeds the screen and the body never scrolls —
    // which is what let the top bar scroll away on mobile. The h-screen
    // class remains as the fallback for browsers without dvh support.
    <div
      className="h-screen flex flex-col overflow-hidden bg-gray-50"
      style={{ height: "100dvh" }}
    >
      {/* Top Bar — frozen: sits outside the scroll container and is sticky
          as a belt-and-braces so it can never leave the viewport */}
      <header className="sticky top-0 bg-darkblue border-b border-white/10 z-40 flex-shrink-0">
        <div className="flex items-center justify-between gap-2 h-16 px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-white hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <img
              src="/bc-logo-transparent.png"
              alt="BidConvey"
              className="h-8 sm:h-10 w-auto max-w-full object-contain cursor-pointer brightness-0 invert"
              onClick={() => navigate("/")}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <HowItWorks
              audience="buyer"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-gold bg-transparent px-2.5 sm:px-3 h-9 text-xs font-semibold text-white hover:bg-white/5"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="bg-transparent hover:bg-white/5 text-white hover:text-white h-9 text-xs font-semibold border border-gold gap-1.5"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — fixed, does not scroll */}
        <aside
          className={`
            fixed top-16 left-0 bottom-0 w-64 bg-darkblue border-r border-white/10
            z-30 transform transition-transform duration-200 ease-in-out overflow-y-auto
            lg:static lg:translate-x-0 lg:z-auto lg:flex-shrink-0
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          <nav className="p-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end || false}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-gold font-semibold"
                      : "text-white hover:text-gold"
                  }`
                }
              >
                {item.icon && <item.icon className="h-4 w-4 flex-shrink-0" />}
                <span className="flex-1">{item.label}</span>
                {item.showCount && quotationsCount !== null && quotationsCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-white text-[11px] font-semibold">
                    {quotationsCount}
                  </span>
                )}
                {item.showSellersChoiceCount && sellersChoiceCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-white text-[11px] font-semibold">
                    {sellersChoiceCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main content — only this scrolls */}
        {/* min-w-0 is load-bearing: without it this flex child refuses to
            shrink below its content's min width, so one wide element would
            push the whole frame sideways instead of scrolling inside itself. */}
        <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto">
          <div className="container mx-auto px-4 py-4 max-w-4xl">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Footer — pinned inside the viewport frame, below sidebar + content */}
      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
}
